// Agents vocaux — the AI agents that answer the phone, a product of their own
// (not a tab of a text agent): each has its greeting, phone instructions,
// voice, tools and number. Created from a template, opened on their own page.
// Backend: app/api/voice_agents.py.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaHeadset, FaPlus } from 'react-icons/fa';
import { TEMPLATES, canManage, createAgent, fail, formatDay, listAgents } from './api';
import './VoiceSetup.css';

const TEMPLATE_DEFAULTS = {
    parcel_support: ['Service client colis', 'Suit un colis à partir de son numéro, ouvre une demande, raccroche.'],
    reception: ['Accueil et prise de message', 'Répond aux questions simples, note le motif et ouvre une demande pour l’équipe.'],
    blank: ['Vierge', 'Aucune consigne : vous écrivez tout.'],
};

const CreateAgent = ({ onCancel, t }) => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [template, setTemplate] = useState('parcel_support');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await createAgent({ name: name.trim(), template });
            navigate(`/voice-agents/${res.data.id}`);
        } catch (err) {
            setError(fail(err, t('voiceSetup.agents.createError', 'L’agent vocal n’a pas pu être créé.')));
            setBusy(false);
        }
    };

    return (
        <form className="vs-buy" onSubmit={submit}>
            <h4>{t('voiceSetup.agents.createTitle', 'Nouvel agent vocal')}</h4>
            <label className="vs-field vs-field--narrow">
                <span>{t('voiceSetup.agents.name', 'Nom')}</span>
                <input value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)}
                    placeholder={t('voiceSetup.agents.namePlaceholder', 'Ex. : Service client')} />
            </label>
            <div className="vs-field">
                <span>{t('voiceSetup.agents.template', 'Point de départ')}</span>
                <div className="vs-templates" role="radiogroup" aria-label={t('voiceSetup.agents.template', 'Point de départ')}>
                    {TEMPLATES.map((key) => (
                        <button key={key} type="button" role="radio" aria-checked={template === key}
                            className={`vs-template ${template === key ? 'vs-template--selected' : ''}`}
                            onClick={() => setTemplate(key)}>
                            <strong>{t(`voiceSetup.agents.templates.${key}.label`, TEMPLATE_DEFAULTS[key][0])}</strong>
                            <span>{t(`voiceSetup.agents.templates.${key}.hint`, TEMPLATE_DEFAULTS[key][1])}</span>
                        </button>
                    ))}
                </div>
            </div>
            {error && <p className="vs-error" role="alert">{error}</p>}
            <div className="vs-actions">
                <button type="submit" className="u-btn u-btn--primary" disabled={!name.trim() || busy}>
                    {busy ? t('voiceSetup.agents.creating', 'Création…') : t('voiceSetup.agents.create', 'Créer l’agent')}
                </button>
                <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>
                    {t('voiceSetup.cancel', 'Annuler')}
                </button>
            </div>
        </form>
    );
};

const AgentCard = ({ agent, t, lang }) => {
    const live = agent.numbers.length > 0;
    const facts = [
        agent.voice ? agent.voice.charAt(0).toUpperCase() + agent.voice.slice(1) : t('voiceSetup.agents.defaultVoice', 'Voix par défaut'),
        agent.language ? t(`voiceSetup.agent.languages.${agent.language}`, agent.language) : t('voiceSetup.agents.anyLanguage', 'Langue de l’appelant'),
        t('voiceSetup.agents.tools', '{{count}} outil(s)', { count: agent.voice_tools }),
    ];
    const gaps = [
        !agent.has_greeting && t('voiceSetup.agents.noGreeting', 'pas d’accueil'),
        !agent.has_instructions && t('voiceSetup.agents.noInstructions', 'pas de consignes'),
        agent.voice_tools === 0 && t('voiceSetup.agents.noTools', 'aucun outil'),
    ].filter(Boolean);
    return (
        <Link to={`/voice-agents/${agent.id}`} className="vs-agent-card">
            <header>
                <span className="vs-number-icon" aria-hidden="true"><FaHeadset /></span>
                <strong>{agent.name}</strong>
                <span className={`vs-chip vs-chip--${live ? 'success' : 'neutral'}`}>
                    {live ? t('voiceSetup.agents.live', 'En ligne') : t('voiceSetup.agents.noNumber', 'Sans numéro')}
                </span>
            </header>
            <p className="vs-agent-card-number">
                {live
                    ? t('voiceSetup.agent.answers', 'Répond au {{numbers}}', { numbers: agent.numbers.map((n) => n.display).join(', ') })
                    : t('voiceSetup.agents.noNumberHint', 'Ne reçoit pas d’appels : donnez-lui un numéro.')}
            </p>
            <p className="vs-agent-card-facts">{facts.join(' · ')}</p>
            {gaps.length > 0 && <p className="vs-warn">{t('voiceSetup.agents.gaps', 'À compléter : {{gaps}}', { gaps: gaps.join(', ') })}</p>}
            <footer>
                <span>
                    {agent.calls_30d > 0
                        ? t('voiceSetup.activity.calls', '{{count}} appel(s) sur 30 jours · dernier le {{date}}', { count: agent.calls_30d, date: formatDay(agent.last_call_at, lang) })
                        : t('voiceSetup.agents.noCalls', 'Aucun appel sur 30 jours')}
                </span>
                <FaArrowRight aria-hidden="true" />
            </footer>
        </Link>
    );
};

const VoiceAgents = () => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const manage = canManage();

    useEffect(() => {
        listAgents()
            .then((res) => setData(res.data))
            .catch((err) => setError(fail(err, t('voiceSetup.agents.loadError', 'Les agents vocaux n’ont pas pu être chargés.'))));
    }, [t]);

    const agents = data?.agents || [];

    return (
        <div className="vs-shell">
            <div className="vs-page">
                <header className="vs-header">
                    <div>
                        <h1>{t('voiceSetup.agents.title', 'Agents vocaux')}</h1>
                        <p>{t('voiceSetup.agents.subtitle', 'Les agents IA qui décrochent le téléphone. Indépendants de vos agents texte : leur accueil, leurs consignes à l’oral, leur voix, leurs outils et leur numéro.')}</p>
                    </div>
                    {manage && agents.length > 0 && !creating && (
                        <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
                            <FaPlus aria-hidden="true" /> {t('voiceSetup.agents.new', 'Nouvel agent vocal')}
                        </button>
                    )}
                </header>

                {error && <p className="vs-error" role="alert">{error}</p>}
                {!data && !error && <p className="vs-note">{t('voiceSetup.loading', 'Chargement…')}</p>}

                {data && data.unrouted_numbers > 0 && agents.length > 0 && (
                    <p className="vs-banner vs-banner--warning">
                        {t('voiceSetup.agents.unrouted', '{{count}} numéro(s) ne renvoie(nt) vers aucun agent : les appels n’y sont pas décrochés.', { count: data.unrouted_numbers })}{' '}
                        <Link className="vs-link" to="/voice-numbers">{t('voiceSetup.agents.routeNumbers', 'Choisir l’agent qui répond')}</Link>
                    </p>
                )}

                {creating && <CreateAgent t={t} onCancel={() => setCreating(false)} />}

                {data && agents.length === 0 && !creating && (
                    <section className="vs-card">
                        <div className="vs-empty">
                            <h4>{t('voiceSetup.agents.emptyTitle', 'Votre premier agent vocal')}</h4>
                            <p>{t('voiceSetup.agents.emptyText', 'Vos clients appellent un numéro, l’agent décroche 24 h/24 : il suit un colis, répond aux questions et ouvre une demande pour votre équipe.')}</p>
                            <ol className="vs-steps vs-steps--inline">
                                {[
                                    t('voiceSetup.agents.step1', 'Créez l’agent à partir d’un modèle'),
                                    t('voiceSetup.agents.step2', 'Donnez-lui un numéro'),
                                    t('voiceSetup.agents.step3', 'Essayez-le dans le navigateur, puis appelez-le'),
                                ].map((text, index) => (
                                    <li key={text} className="vs-step">
                                        <span className="vs-step-mark" aria-hidden="true">{index + 1}</span>
                                        <div><strong>{text}</strong></div>
                                    </li>
                                ))}
                            </ol>
                            {manage && (
                                <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
                                    <FaPlus aria-hidden="true" /> {t('voiceSetup.agents.new', 'Nouvel agent vocal')}
                                </button>
                            )}
                        </div>
                    </section>
                )}

                {agents.length > 0 && (
                    <div className="vs-agent-cards">
                        {agents.map((agent) => <AgentCard key={agent.id} agent={agent} t={t} lang={i18n.language} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceAgents;
